import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDojoMode } from "@/plugins/vibeDojo/DojoContext";

// Pre-computed once so positions/timings are stable across renders.
const PETALS = Array.from({ length: 16 }).map((_, i) => ({
  id: i,
  left: Math.random() * 100,
  size: 11 + Math.random() * 13,
  dur: 12 + Math.random() * 12,
  delay: -Math.random() * 20,
  drift: (Math.random() * 2 - 1) * 90,
  op: 0.45 + Math.random() * 0.35,
  hue: Math.random() < 0.5 ? "#F9A8D4" : "#F472B6",
}));

// Single global immersive Dojo shell. Wraps the whole app so that whenever
// dojoMode is on, EVERY page adopts the identical high-end zen-garden treatment
// (garden scene + falling petals + dark palette) — no per-route gating. This is
// the one place the immersive visuals live; pages themselves stay untouched.
export function DojoLayout({ enabled = true, children }) {
  const { dojoMode, setDojoMode, sceneSrc } = useDojoMode();
  const { pathname } = useLocation();
  // The immersive Dojo (garden + zen palette + ambient audio) renders ONLY on the
  // dedicated /dojo page. It NEVER overlays any other page (no Landing hybrid).
  const active = enabled && dojoMode && pathname === "/dojo";

  // Leaving /dojo cleanly turns Dojo Mode OFF so audio stops and no page inherits
  // the garden.
  useEffect(() => {
    if (pathname !== "/dojo" && dojoMode) setDojoMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const [mounted, setMounted] = useState(active);
  const [visible, setVisible] = useState(active);

  useEffect(() => {
    if (active) {
      document.body.classList.add("dojo-scene");
      setMounted(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    document.body.classList.remove("dojo-scene");
    const t = setTimeout(() => setMounted(false), 2100);
    return () => clearTimeout(t);
  }, [active]);

  const petals = useMemo(() => PETALS, []);

  return (
    <>
      {mounted && (
        <>
          <div
            aria-hidden="true"
            data-testid="dojo-background"
            className="fixed inset-0 z-[-1] overflow-hidden"
            style={{ opacity: visible ? 1 : 0, transition: "opacity 2s ease" }}
          >
            <img
              src={sceneSrc}
              alt=""
              className="absolute inset-0 w-full h-full object-cover animate-dojo-pan"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(6,10,8,0.78) 0%, rgba(6,10,8,0.52) 45%, rgba(6,10,8,0.74) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 90% at 50% 40%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.5) 100%)",
              }}
            />
          </div>

          <div
            aria-hidden="true"
            data-testid="dojo-petals"
            className="fixed inset-0 z-[35] pointer-events-none overflow-hidden"
            style={{ opacity: visible ? 1 : 0, transition: "opacity 2s ease" }}
          >
            {petals.map((p) => (
              <span
                key={p.id}
                className="dojo-petal"
                style={{
                  left: `${p.left}%`,
                  "--sz": `${p.size}px`,
                  "--dur": `${p.dur}s`,
                  "--delay": `${p.delay}s`,
                  "--drift": `${p.drift}px`,
                  "--op": p.op,
                  "--hue": p.hue,
                }}
              />
            ))}
          </div>
        </>
      )}
      {children}
    </>
  );
}
